/**
 * Announcement Service Hooks
 * React hooks for accessing announcement management services
 */
import { useCCMS } from '../useCCMS';
/**
 * Hook to access all announcement-related services
 * @returns AnnouncementServices object containing all announcement service instances
 */
export function useAnnouncementsServices() {
    const { services } = useCCMS();
    if (!services?.announcements) {
        throw new Error('Announcement services not available. Ensure CCMSProvider is configured with announcement services.');
    }
    return services.announcements;
}
/**
 * Hook to access AnnouncementService
 */
export function useAnnouncementService() {
    return useAnnouncementsServices().announcement;
}
/**
 * Hook to access AnnouncementStatusService
 */
export function useAnnouncementStatusService() {
    return useAnnouncementsServices().announcementStatus;
}
/**
 * Hook to access AnnouncementTypeService
 */
export function useAnnouncementTypeService() {
    return useAnnouncementsServices().announcementType;
}
/**
 * Hook to access AnnouncementCategoryService
 */
export function useAnnouncementCategoryService() {
    return useAnnouncementsServices().announcementCategory;
}
/**
 * Hook to access AnnouncementPriorityService
 */
export function useAnnouncementPriorityService() {
    return useAnnouncementsServices().announcementPriority;
}
/**
 * Hook to access AnnouncementAudienceService
 */
export function useAnnouncementAudienceService() {
    return useAnnouncementsServices().announcementAudience;
}
/**
 * Hook to access AnnouncementMediaTypeService
 */
export function useAnnouncementMediaTypeService() {
    return useAnnouncementsServices().announcementMediaType;
}
/**
 * Hook to access RecurrenceTypeService
 */
export function useRecurrenceTypeService() {
    return useAnnouncementsServices().recurrenceType;
}
/**
 * Hook to access AnnouncementLinkTypeService
 */
export function useAnnouncementLinkTypeService() {
    return useAnnouncementsServices().announcementLinkType;
}
/**
 * Hook to access AnnouncementTagLookupService
 */
export function useAnnouncementTagLookupService() {
    return useAnnouncementsServices().announcementTagLookup;
}
/**
 * Hook to access AnnouncementLocationService
 */
export function useAnnouncementLocationService() {
    return useAnnouncementsServices().announcementLocation;
}
/**
 * Hook to access AnnouncementEventService
 */
export function useAnnouncementEventService() {
    return useAnnouncementsServices().announcementEvent;
}
/**
 * Hook to access AnnouncementMediaService
 */
export function useAnnouncementMediaService() {
    return useAnnouncementsServices().announcementMedia;
}
/**
 * Hook to access AnnouncementLinkService
 */
export function useAnnouncementLinkService() {
    return useAnnouncementsServices().announcementLink;
}
/**
 * Hook to access AnnouncementTagService
 */
export function useAnnouncementTagService() {
    return useAnnouncementsServices().announcementTag;
}
/**
 * Hook to access AnnouncementLogService (read-only)
 */
export function useAnnouncementLogService() {
    return useAnnouncementsServices().announcementLog;
}
/**
 * Hook to access AnnouncementAnalyticService
 */
export function useAnnouncementAnalyticService() {
    return useAnnouncementsServices().announcementAnalytic;
}
/**
 * Hook to access PublicAnnouncementService (read-only public endpoints)
 */
export function usePublicAnnouncementService() {
    return useAnnouncementsServices().publicAnnouncement;
}
//# sourceMappingURL=useAnnouncementsServices.js.map